import React, { useState, useEffect } from 'react';
import { isDevEnvironment, isBetaEnvironment, isAdminUser } from '../../config/environment';
import DevAccessDenied from './DevAccessDenied';
import DevAdminLoginMinimal from './DevAdminLoginMinimal';
import BetaAccessDenied from '../EarlyAccess/BetaAccessDenied';
import earlyAccessService from '../../services/earlyAccess';

interface DevProtectionWrapperProps {
  children: React.ReactNode;
}

const DevProtectionWrapper: React.FC<DevProtectionWrapperProps> = ({ children }) => {
  const [isDevAuthenticated, setIsDevAuthenticated] = useState<boolean>(false);
  const [isBetaAuthenticated, setIsBetaAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPath, setCurrentPath] = useState<string>('');

  //console.log('🔍 DevProtectionWrapper Debug:');
  //console.log('Current path:', window.location.pathname);
  //console.log('VITE_ENV:', import.meta.env.VITE_ENV);
  console.log('isDevEnvironment():', isDevEnvironment());
  console.log('isBetaEnvironment():', isBetaEnvironment());
  console.log('isDevAuthenticated:', isDevAuthenticated);
  console.log('isBetaAuthenticated:', isBetaAuthenticated);
  //console.log('isLoading:', isLoading);

  const checkAdminAccess = (user: any): boolean => {
    return isAdminUser(user);
  };

  useEffect(() => {
    //console.log('🔍 DevProtectionWrapper useEffect triggered');
    
    // Get current path
    setCurrentPath(window.location.pathname);
    //console.log('Setting current path to:', window.location.pathname);
    
    // Check stored user data to see if they're already logged in as admin
    const storedUser = localStorage.getItem('authUser');
    const storedToken = localStorage.getItem('authToken');
    
    //console.log('Stored user:', storedUser);
    //console.log('Stored token exists:', !!storedToken);
    
    // If user is already logged in and is admin, grant access immediately
    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        //console.log('Parsed user:', user);
        //console.log('User role:', user.role);
        //console.log('checkAdminAccess result:', checkAdminAccess(user));
        
        if (checkAdminAccess(user)) {
          console.log('✅ User is admin, granting access');
          sessionStorage.setItem('dev_authenticated', 'true');
          setIsDevAuthenticated(true);
          setIsLoading(false);
          return;
        }
      } catch (error) {
        //console.error('Error parsing stored user:', error);
      }
    }
    
    // If not dev environment, check for beta environment
    if (!isDevEnvironment() && !isBetaEnvironment()) {
      console.log('✅ Not dev or beta environment, skipping protection');
      setIsDevAuthenticated(true);
      setIsBetaAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Handle beta environment
    if (isBetaEnvironment()) {
      console.log('🔍 In beta environment, checking beta auth');
      const betaAuth = earlyAccessService.isBetaAuthenticated();
      setIsBetaAuthenticated(betaAuth);
      console.log('Beta auth status:', betaAuth);
    }

    // Handle dev environment
    if (isDevEnvironment()) {
      console.log('🔍 In dev environment, checking dev auth');

      // In dev environment, check if already dev authenticated
      const devAuth = sessionStorage.getItem('dev_authenticated');
      //console.log('Dev auth session:', devAuth);
      
      if (devAuth === 'true' && storedUser && storedToken) {
        try {
          const user = JSON.parse(storedUser);
          if (checkAdminAccess(user)) {
            //console.log('✅ Dev auth valid, user is admin');
            setIsDevAuthenticated(true);
          } else {
            console.log('❌ User not admin, clearing session');
            // Clear invalid session
            sessionStorage.removeItem('dev_authenticated');
            setIsDevAuthenticated(false);
          }
        } catch (error) {
          console.log('❌ Error parsing user, clearing session');
          // Clear corrupted session
          sessionStorage.removeItem('dev_authenticated');
          setIsDevAuthenticated(false);
        }
      } else {
        console.log('❌ No valid dev auth found');
        setIsDevAuthenticated(false);
      }
    }
    
    setIsLoading(false);
    //console.log('🔍 useEffect complete, isDevAuthenticated:', isDevAuthenticated);
  }, []);

  // Listen for route changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const handleLogin = () => {
    setIsDevAuthenticated(true);
  };

  // Show loading state briefly
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto"></div>
          <p className="mt-2 text-cyan-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If not dev or beta environment, render children normally
  if (!isDevEnvironment() && !isBetaEnvironment()) {
    return <>{children}</>;
  }

  // Beta environment logic
  if (isBetaEnvironment()) {
    const allowedBetaRoutes = ['/soft-launch', '/early-access'];
    
    // Always allow access to the allowed beta routes
    if (allowedBetaRoutes.includes(currentPath)) {
      console.log('✅ Allowing access to beta route:', currentPath);
      return <>{children}</>;
    }
    
    // For other routes, check if user is beta authenticated
    if (!isBetaAuthenticated) {
      console.log('🚫 Showing beta access denied for route:', currentPath);
      return <BetaAccessDenied />;
    }
    
    // If beta authenticated, allow access to all other routes
    console.log('✅ Beta authenticated, allowing access to:', currentPath);
    return <>{children}</>;
  }

  // Dev environment logic
  if (isDevEnvironment()) {
    // If not authenticated
    if (!isDevAuthenticated) {
      console.log('🔍 Not dev authenticated, checking what to render');
      console.log('isDevEnvironment():', isDevEnvironment());
      
      // For admin route, show login if it's a dev environment OR user isn't admin
      if (currentPath === '/admin') {
        //console.log('📝 Showing admin login for /admin route');
        return <DevAdminLoginMinimal onLogin={handleLogin} />;
      }
      
      // For non-admin routes in dev environment, show access denied
      console.log('🚫 Showing access denied for dev environment');
      return <DevAccessDenied />;
    }
    
    // If authenticated in dev environment, render children
    //console.log('✅ Dev authenticated, rendering children');
    return <>{children}</>;
  }

  // Default fallback
  return <>{children}</>;
};

export default DevProtectionWrapper;
